import type {
  BazelBuild,
  BazelInvocationMeta,
} from "@/client/director/buildLogs.tsx";
import { ExecStatusBadge } from "@/components/BuildLogs";
import { formatDurationMs, formatRelativeTime } from "@/lib/format";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  INSIGHTS_WINDOW_DAYS,
  summarize,
  targets,
  trend,
  type TrendPoint,
} from "@/lib/bazel-insights";
import { barToneClass, targetOf } from "@/lib/bazel";
import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";

// The same tones as the build overview bars: cache hits green, remote
// execution blue, Bazel's own bookkeeping grey.
const chartConfig = {
  cachedS: { label: "From cache", color: "rgb(16 185 129 / 0.7)" },
  remoteS: { label: "Ran on workers", color: "rgb(59 130 246 / 0.7)" },
  otherS: {
    label: "Inside Bazel or locally",
    color: "rgb(148 163 184 / 0.55)",
  },
} satisfies ChartConfig;

const number = new Intl.NumberFormat();
const dayFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

const pct = (part: number, whole: number) => Math.round((part / whole) * 100);

// axisDuration keeps axis ticks to one short unit, so labels never wrap.
function axisDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function Stat({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1 px-6 py-4">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-2xl font-semibold tracking-tight tabular-nums">
        {value}
      </span>
      {detail && (
        <span className="truncate text-xs text-muted-foreground">{detail}</span>
      )}
    </div>
  );
}

function TrendTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: TrendPoint }[];
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const cachedPct = point.durationS
    ? Math.round((point.cachedS / point.durationS) * 100)
    : 0;
  return (
    <div className="flex max-w-xs flex-col gap-1 rounded-md border bg-background px-3 py-2 text-xs shadow-sm">
      <span className="truncate font-mono font-medium">{point.label}</span>
      <span className="text-muted-foreground">
        {new Date(point.startedAt).toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <ExecStatusBadge status={point.status} />
        <span className="tabular-nums">
          {formatDurationMs(point.durationS * 1000)}
        </span>
        <span className="text-muted-foreground">
          {cachedPct}% of actions from cache
        </span>
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {Object.entries(chartConfig).map(([key, { label, color }]) => (
        <span key={key} className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-sm"
            style={{ background: color }}
          />
          {label}
        </span>
      ))}
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm border-2 border-red-500" />
        Failed
      </span>
    </div>
  );
}

export function BazelInsights({
  builds,
  metas,
  onSelectBuild,
}: {
  builds: BazelBuild[];
  metas: Map<string, BazelInvocationMeta> | "loading";
  onSelectBuild: (id: string) => void;
}) {
  // Polls rotate every build's presigned URLs, so recompute only when a
  // build's state or the summaries change.
  const signature = builds
    .map((b) =>
      [b.id, b.status, b.durationMs, b.startedAt, targetOf(b)].join(":"),
    )
    .join(",");
  const { summary, points, rows } = useMemo(() => {
    const known =
      metas === "loading" ? new Map<string, BazelInvocationMeta>() : metas;
    const points = trend(builds, known);
    return {
      summary: summarize(builds, known),
      points,
      rows: targets(builds, known),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- signature stands for builds
  }, [signature, metas]);
  // Axis labels are relative times, so they are worked out on every render.
  // One axis label per day, on that day's first build; the tooltip carries
  // each build's exact time.
  const dayTicks = new Map<string, string>();
  for (const p of points) {
    const day = dayFormat.format(new Date(p.startedAt));
    if (![...dayTicks.values()].includes(day)) dayTicks.set(p.id, day);
  }
  const hitRate = summary.cacheable
    ? Math.round((summary.cacheHits / summary.cacheable) * 100)
    : undefined;
  const successRate = summary.finished
    ? Math.round((summary.succeeded / summary.finished) * 100)
    : undefined;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        The last {INSIGHTS_WINDOW_DAYS} days of builds.
      </p>
      <Card className="py-0">
        <div className="flex flex-col divide-y sm:flex-row sm:divide-x sm:divide-y-0">
          <Stat
            label="Builds"
            value={number.format(summary.builds)}
            detail={
              successRate != null ? `${successRate}% succeeded` : undefined
            }
          />
          <Stat
            label="Remote cache hit rate"
            value={hitRate != null ? `${hitRate}%` : "-"}
            detail={
              summary.cacheable
                ? `${number.format(summary.cacheHits)} of ${number.format(summary.cacheable)} cacheable actions`
                : undefined
            }
          />
          <Stat
            label="Actions run on workers"
            value={
              metas === "loading" ? "-" : number.format(summary.remoteActions)
            }
            detail="On Limrun's Macs and Linux workers"
          />
          <Stat
            label="Median build time"
            value={formatDurationMs(summary.medianDurationMs) ?? "-"}
            detail={
              summary.finished
                ? `Over ${number.format(summary.finished)} finished builds`
                : undefined
            }
          />
        </div>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1.5">
            <CardTitle>Build time</CardTitle>
            <CardDescription>
              Each bar is one build, split by where its actions ran. Select a
              bar to open the build.
            </CardDescription>
          </div>
          <Legend />
        </CardHeader>
        <CardContent>
          {points.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No finished builds in this window.
            </p>
          ) : (
            <ChartContainer
              config={chartConfig}
              className="aspect-auto h-56 w-full"
            >
              <BarChart
                data={points}
                margin={{ left: 4, right: 4, top: 8 }}
                barCategoryGap="20%"
                maxBarSize={56}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="id"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  ticks={[...dayTicks.keys()]}
                  interval="preserveStartEnd"
                  tickFormatter={(id: string) => dayTicks.get(id) ?? ""}
                />
                <YAxis
                  width={40}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={axisDuration}
                />
                <ChartTooltip
                  cursor={{ fill: "hsl(var(--muted))" }}
                  content={<TrendTooltip />}
                />
                {(["cachedS", "remoteS", "otherS"] as const).map(
                  (key, i, keys) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="build"
                      isAnimationActive={false}
                      // A cached build of a second still shows as a sliver.
                      minPointSize={(value) => ((value ?? 0) > 0 ? 2 : 0)}
                      fill={`var(--color-${key})`}
                      radius={i === keys.length - 1 ? [3, 3, 0, 0] : 0}
                      className="cursor-pointer"
                      onClick={(data: { payload?: TrendPoint }) =>
                        data.payload && onSelectBuild(data.payload.id)
                      }
                    >
                      {points.map((p) => (
                        <Cell
                          key={p.id}
                          stroke={p.failed ? "rgb(239 68 68)" : undefined}
                          strokeWidth={p.failed ? 1.5 : 0}
                        />
                      ))}
                    </Bar>
                  ),
                )}
              </BarChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Targets</CardTitle>
          <CardDescription>
            What your organization builds most, and how much of it comes from
            the cache.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Target</TableHead>
                <TableHead className="text-right">Builds</TableHead>
                <TableHead className="hidden text-right sm:table-cell">
                  Median time
                </TableHead>
                <TableHead className="pr-6 text-right sm:w-48 sm:pr-2 sm:text-left">
                  Cache hit rate
                </TableHead>
                <TableHead className="hidden pr-6 md:table-cell">
                  Last build
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.target}>
                  <TableCell
                    className="max-w-40 truncate pl-6 sm:max-w-72 font-mono text-xs"
                    title={row.target}
                  >
                    {row.target}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.builds}
                  </TableCell>
                  <TableCell className="hidden text-right tabular-nums sm:table-cell">
                    {formatDurationMs(row.medianDurationMs) ?? "-"}
                  </TableCell>
                  <TableCell>
                    {!row.cacheable ? (
                      <span className="text-muted-foreground">-</span>
                    ) : (
                      <div className="flex items-center justify-end gap-2 pr-4 sm:justify-start sm:pr-0">
                        <div className="hidden h-1.5 flex-1 overflow-hidden rounded-full bg-muted sm:block">
                          <div
                            className={`h-full rounded-full ${barToneClass.hit}`}
                            style={{
                              width: `${pct(row.cacheHits, row.cacheable)}%`,
                            }}
                          />
                        </div>
                        <span className="w-9 text-right text-xs tabular-nums">
                          {pct(row.cacheHits, row.cacheable)}%
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="hidden pr-6 md:table-cell">
                    <div className="flex items-center gap-2">
                      <ExecStatusBadge status={row.lastStatus} />
                      {row.lastStartedAt && (
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(row.lastStartedAt)}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
